import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCart";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { totalCount } = useCart();

  // Close admin dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".admin-dropdown")) {
        setAdminMenuOpen(false);
      }
    };
    if (adminMenuOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [adminMenuOpen]);

  const navLinks = [
    { label: "全部貨源", href: "/products", external: false },
    { label: "體驗認購區", href: "/products?region=體驗認購區", external: false },
    { label: "VIP認購區", href: "/products?region=VIP認購區", external: false },
    { label: "吉時好物", href: "/products?region=吉時好物", external: false },
    { label: "儲值中心", href: "/dashboard/recharge", external: false },
  ];

  const adminLinks = [
    { label: "管理總覽", href: "/admin/dashboard" },
    { label: "儲值審核", href: "/admin/recharge" },
    { label: "提現審核", href: "/admin/withdrawals" },
    { label: "實名審核", href: "/admin/kyc" },
    { label: "商品管理", href: "/admin/products" },
    { label: "訂單管理", href: "/admin/orders" },
    { label: "會員管理", href: "/admin/members" },
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100"
    >
      <div className="px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/823a8f074b021e82ba2884013917a5c5/a23db1fedd75cee41aae25a9154ab297.jpeg"
              alt="吉時國際貿易 跨境分銷平台"
              className="h-10 w-auto object-contain rounded"
            />
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
              首頁
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Search + Auth */}
          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="搜尋跨境貨源..."
                className="pl-9 pr-3 py-1.5 rounded-full text-sm w-44 focus:w-56 transition-all duration-300 outline-none border bg-gray-100 border-gray-200 text-gray-800 placeholder-gray-400"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm text-gray-400"></i>
              </div>
            </form>

            {/* Cart */}
            <Link
              to="/dashboard/cart"
              className="relative w-8 h-8 flex items-center justify-center rounded-full text-gray-600 hover:text-amber-600 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-cart-line text-lg"></i>
              {totalCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                  {totalCount > 99 ? "99+" : totalCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="flex items-center gap-2">
                {isAdmin && (
                <div className="relative admin-dropdown">
                  <button
                    onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-dashboard-line text-xs"></i>
                    </div>
                    後台管理
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-xs transition-transform ${adminMenuOpen ? "rotate-180" : ""}`}></i>
                    </div>
                  </button>
                  {adminMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                      {adminLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-amber-600 transition-colors cursor-pointer"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                )}

                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                    <i className="ri-user-line text-amber-600 text-xs"></i>
                  </div>
                  {user.name}
                </Link>
                <button
                  onClick={logout}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                  title="登出"
                >
                  <i className="ri-logout-box-r-line text-sm"></i>
                </button>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-1.5 rounded-full text-sm font-medium border border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  分銷商登入
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-1.5 rounded-full text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  立即註冊
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden w-8 h-8 flex items-center justify-center text-gray-700 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4">
          <form onSubmit={handleSearch} className="relative mb-4">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="搜尋跨境貨源..."
              className="w-full pl-9 pr-3 py-2 rounded-full text-sm bg-gray-100 border border-gray-200 text-gray-800 placeholder-gray-400 outline-none"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-sm text-gray-400"></i>
            </div>
          </form>
          <nav className="flex flex-col gap-3 mb-4">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-gray-700 hover:text-amber-600 cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm font-medium text-gray-700 hover:text-amber-600 cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>
          <div className="flex flex-col gap-3">
            <Link
              to="/dashboard/cart"
              className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 hover:text-amber-600 cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-cart-line text-sm"></i>
              </div>
              購物車 {totalCount > 0 && <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">{totalCount}</span>}
            </Link>
            {user ? (
              <>
                {isAdmin && (
                <>
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 hover:text-amber-600 cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-dashboard-line text-sm"></i>
                    </div>
                    管理後台
                  </Link>
                </>
                )}
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 hover:text-amber-600 cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-sm"></i></div>
                  個人中心 ({user.name})
                </Link>
                <button
                  onClick={() => { logout(); setMobileOpen(false); }}
                  className="flex items-center gap-2 py-2 text-sm font-medium text-red-500 cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-r-line text-sm"></i></div>
                  登出
                </button>
              </>
            ) : (
              <div className="flex gap-3">
                <Link
                  to="/login"
                  className="flex-1 py-2 rounded-full text-sm font-medium border border-amber-500 text-amber-600 text-center cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  分銷商登入
                </Link>
                <Link
                  to="/register"
                  className="flex-1 py-2 rounded-full text-sm font-medium bg-amber-500 text-white text-center cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  立即註冊
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
